import { useQuery } from "@apollo/client"
import { ArrowRight, MoreHorizontal } from "lucide-react"
import { Link, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import {
  editLettermemePath,
  lettermemeNewsletterInputsPath,
  lettermemeSourcesPath,
  lettermemeSubscribersPath,
  lettermemeTrackingCodePath,
  myLettermemeMonetizationPath,
  setupLettermemePath,
} from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/ui/dropdown-menu"
import { GraphqlError } from "~/ui/errors"
import { ScreenLoading } from "~/ui/screen-loading"
import Text from "~/ui/typography"
import { useToast } from "~/ui/use-toast"

const lettermemeManageQuery = gql(/* GraphQL */ `
  query MyLettermemeManage($id: ID!) {
    lettermeme(id: $id) {
      id
      name
      urlPath
      updateCadence
      emailCadence
      subscriptionPriceCents
      subscriptionCurrency
      publicDescription
    }
  }
`)

const generateSummaryMutation = gql(/* GraphQL */ `
  mutation GenerateLettermemeSummaryFromManage($input: LettermemeGenerateSummaryInput!) {
    lettermemeGenerateSummary(input: $input) {
      lettermeme {
        id
        name
      }
    }
  }
`)

const formatCadence = (value: string) => {
  const lower = value.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

const formatMonthlyPrice = (subscriptionPriceCents: number | null | undefined) => {
  if (!subscriptionPriceCents) return "$0"
  return `$${(subscriptionPriceCents / 100).toFixed(2)}`
}

const ManageRowLink = ({ to, label }: { to: string; label: string }) => {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-md border border-gray-700 px-4 py-3 text-gray-200 hover:border-gray-500"
    >
      <span>{label}</span>
      <ArrowRight className="h-4 w-4 text-gray-400" />
    </Link>
  )
}

export const MyLettermemeManageScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()

  const { data, error, loading } = useQuery(lettermemeManageQuery, {
    variables: { id: id! },
  })

  const lettermeme = data?.lettermeme

  useDocumentTitle(lettermeme ? lettermeme.name : "My Lettermeme")

  const [generateSummary, { loading: summaryLoading }] = useSafeMutation(generateSummaryMutation, {
    onCompleted: () => {
      toast({
        title: "Summary generation started",
        description: "We'll send you an email when it's ready.",
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Lettermeme not found
        </Text>
        <Text>The lettermeme you're looking for doesn't exist.</Text>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <Text as="h1" className="text-2xl font-bold">
            {lettermeme.name}
          </Text>
          <div className="mt-4 space-y-1 text-sm text-gray-300">
            <div>URL path - /{lettermeme.urlPath}</div>
            <div>Update cadence: {formatCadence(lettermeme.updateCadence)}</div>
            <div>Email cadence: {formatCadence(lettermeme.emailCadence)}</div>
            <div>Monthly Subscription: {formatMonthlyPrice(lettermeme.subscriptionPriceCents)}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link to={editLettermemePath({ id: lettermeme.id })}>
            <Button variant="outline">edit settings</Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="More actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  generateSummary({ variables: { input: { lettermemeId: lettermeme.id } } })
                }
                disabled={summaryLoading}
                className="cursor-pointer"
              >
                Generate summary now
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="mb-8">
        <Text className="mb-2 text-sm text-gray-400">Public Description:</Text>
        <Text className="text-gray-200">{lettermeme.publicDescription || "—"}</Text>
      </div>

      <div className="space-y-3">
        <ManageRowLink to={lettermemeSubscribersPath({ id: lettermeme.id })} label="subscribers" />
        <ManageRowLink
          to={myLettermemeMonetizationPath({ id: lettermeme.id })}
          label="monetization"
        />
        <ManageRowLink to={lettermemeSourcesPath({ id: lettermeme.id })} label="sources" />
        <ManageRowLink to={lettermemeNewsletterInputsPath({ id: lettermeme.id })} label="inputs" />
        <ManageRowLink
          to={lettermemeTrackingCodePath({ id: lettermeme.id })}
          label="tracking codes"
        />
        <ManageRowLink to={setupLettermemePath({ id: lettermeme.id })} label="instructions" />
      </div>
    </div>
  )
}
